/**
 * useAssetAnalysis Hook
 *
 * Analyzes trading performance by asset and token category.
 */

import { useMemo } from "react";
import type {
  CompletedTrade,
  AssetAnalysis,
  AssetPerformance,
  TokenCategory,
} from "@nordex/core";
import { getCategoryLabel } from "@nordex/core";

/**
 * Calculate performance metrics for a group of trades
 */
function calculatePerformance(
  id: string,
  label: string,
  trades: CompletedTrade[]
): AssetPerformance {
  if (!trades.length) {
    return {
      id,
      label,
      pnl: 0,
      trades: 0,
      wins: 0,
      winRate: 0,
      volume: 0,
      roi: 0,
      profitFactor: 0,
    };
  }

  let totalPnl = 0;
  let totalVolume = 0;
  let totalProfit = 0;
  let totalLoss = 0;
  let wins = 0;

  for (const trade of trades) {
    totalPnl += trade.netPnl;
    totalVolume += trade.volume;

    if (trade.netPnl > 0) {
      wins++;
      totalProfit += trade.netPnl;
    } else if (trade.netPnl < 0) {
      totalLoss += Math.abs(trade.netPnl);
    }
  }

  const tradeCount = trades.length;
  const winRate = tradeCount > 0 ? (wins / tradeCount) * 100 : 0;
  const roi = totalVolume > 0 ? (totalPnl / totalVolume) * 100 : 0;
  const profitFactor =
    totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? Infinity : 0;

  return {
    id,
    label,
    pnl: totalPnl,
    trades: tradeCount,
    wins,
    winRate,
    volume: totalVolume,
    roi,
    profitFactor,
  };
}

/**
 * Extract base symbol from market (e.g., "BTC-USD" -> "BTC")
 */
function getBaseSymbol(market: string): string {
  return market.split("-")[0] || market;
}

/**
 * Calculate asset-based analytics
 */
function calculateAssetAnalysis(trades: CompletedTrade[]): AssetAnalysis {
  // Group trades by market
  const byMarketMap = new Map<string, CompletedTrade[]>();
  for (const trade of trades) {
    const existing = byMarketMap.get(trade.market) || [];
    existing.push(trade);
    byMarketMap.set(trade.market, existing);
  }

  // Group trades by category
  const byCategoryMap = new Map<TokenCategory, CompletedTrade[]>();
  for (const trade of trades) {
    const existing = byCategoryMap.get(trade.category) || [];
    existing.push(trade);
    byCategoryMap.set(trade.category, existing);
  }

  // Calculate per-market performance
  const byMarket: AssetPerformance[] = [];
  for (const [market, marketTrades] of byMarketMap) {
    byMarket.push(
      calculatePerformance(market, getBaseSymbol(market), marketTrades)
    );
  }

  // Sort by P&L descending
  byMarket.sort((a, b) => b.pnl - a.pnl);

  // Calculate per-category performance
  const byCategory: AssetPerformance[] = [];
  for (const [category, categoryTrades] of byCategoryMap) {
    byCategory.push(
      calculatePerformance(category, getCategoryLabel(category), categoryTrades)
    );
  }

  // Sort by P&L descending
  byCategory.sort((a, b) => b.pnl - a.pnl);

  // Find best/worst
  const marketsWithTrades = byMarket.filter((m) => m.trades > 0);
  const categoriesWithTrades = byCategory.filter((c) => c.trades > 0);

  const bestMarket = marketsWithTrades[0] || null;
  const worstMarket =
    marketsWithTrades.length > 0
      ? marketsWithTrades[marketsWithTrades.length - 1]
      : null;
  const bestCategory = categoriesWithTrades[0] || null;

  return {
    byMarket,
    byCategory,
    bestMarket,
    worstMarket,
    bestCategory,
  };
}

/**
 * Hook for asset-based trading analysis
 */
export function useAssetAnalysis(trades: CompletedTrade[]): AssetAnalysis {
  return useMemo(() => calculateAssetAnalysis(trades), [trades]);
}

/**
 * Get top N performing assets
 */
export function getTopAssets(
  analysis: AssetAnalysis,
  n: number = 5
): AssetPerformance[] {
  return analysis.byMarket.filter((a) => a.trades > 0).slice(0, n);
}

/**
 * Get bottom N performing assets
 */
export function getBottomAssets(
  analysis: AssetAnalysis,
  n: number = 5
): AssetPerformance[] {
  const withTrades = analysis.byMarket.filter((a) => a.trades > 0);
  return withTrades.slice(-n).reverse();
}

